import { useCallback, useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Home as HomeIcon, BookOpen, GraduationCap, Heart, Receipt, MessageCircle, ShoppingCart, Languages, LogOut, ArrowRight, CalendarClock, Trophy } from 'lucide-react';
import Brand from '../components/Brand';
import ModeToggle from '../components/ModeToggle';
import NotificationsBell from '../components/NotificationsBell';
import { authAPI, engageAPI, publicAPI, shopAPI } from '../lib/api';
import { clearSession, getUser, isLoggedIn } from '../lib/auth';
import { getLang, setLang, t } from '../lib/i18n';

// Public storefront chrome — the IMS "Velzon" register: a navy command sidebar
// (brand + primary nav) + a white topbar (utilities + account) + a light content
// canvas, so the LMS matches the IMS/Morning-Brief tools. Students/anonymous
// share this shell; staff get an extra Back-office link.

const PAGE_TITLES = [
  [/^\/courses/, 'Courses'],
  [/^\/course\//, 'Course'],
  [/^\/my\/courses/, 'My courses'],
  [/^\/my\/wishlist/, 'Wishlist'],
  [/^\/my\/messages/, 'Messages'],
  [/^\/my\/mentor/, 'Mentor calendar'],
  [/^\/my\/purchases/, 'Purchases'],
  [/^\/cart/, 'Cart'],
  [/^\/instructor/, 'Instructor'],
  [/^\/p\//, ''],
  [/^\/$/, 'Home'],
];

export default function PublicShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();
  const staff = ['admin', 'manager'].includes(user?.lr);
  const [pages, setPages] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [msgUnread, setMsgUnread] = useState(0);
  const [navOpen, setNavOpen] = useState(false); // phone/tablet drawer
  useEffect(() => { setNavOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!isLoggedIn()) return;
    engageAPI.threads().then(({ data }) => setMsgUnread(data.unread || 0)).catch(() => {});
  }, []);

  useEffect(() => {
    publicAPI.frontend().then(({ data }) => setPages(data.pages || [])).catch(() => {});
  }, []);

  const refreshCart = useCallback(() => {
    if (!isLoggedIn()) return;
    shopAPI.cart().then(({ data }) => setCartCount((data.courses || []).length)).catch(() => {});
  }, []);
  useEffect(() => {
    refreshCart();
    window.addEventListener('lms-cart-changed', refreshCart);
    return () => window.removeEventListener('lms-cart-changed', refreshCart);
  }, [refreshCart]);

  const signOut = async () => {
    try {
      await authAPI.logout();
    } catch {
      /* stateless */
    }
    clearSession();
    navigate('/login', { replace: true });
  };

  const fullBleed = /^\/(course\/|my\/(courses|wishlist|purchases|points)(\/|$))/.test(location.pathname);
  const pageTitle = t((PAGE_TITLES.find(([re]) => re.test(location.pathname)) || [null, ''])[1]);

  // Navy-sidebar link: icon + label, teal left-border + subtle wash when active.
  const NavItem = ({ to, end, icon: Icon, label, badge }) => (
    <NavLink
      to={to}
      end={end}
      style={({ isActive }) => ({
        display: 'flex', alignItems: 'center', gap: 11,
        padding: '9px 11px', borderRadius: 6, fontSize: 13.5, fontWeight: 500,
        textDecoration: 'none',
        color: isActive ? '#ffffff' : '#abb9e8',
        background: isActive ? 'rgba(255,255,255,0.10)' : 'transparent',
        borderLeft: `3px solid ${isActive ? 'var(--lms-accent)' : 'transparent'}`,
      })}
    >
      <Icon size={17} />
      <span style={{ flex: 1 }}>{label}</span>
      {badge > 0 && (
        <span className="lms-num" style={{ minWidth: 18, height: 18, padding: '0 5px', borderRadius: 20, background: 'var(--lms-accent)', color: '#fff', fontSize: 10.5, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {msgUnread}
        </span>
      )}
    </NavLink>
  );

  const sectionLbl = { color: '#7889c9', fontSize: 10, fontWeight: 600, letterSpacing: '.13em', padding: '4px 8px 8px' };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--lms-bg)' }}>
      {/* ============ SIDEBAR ============ */}
      <aside className={`lms-side${navOpen ? ' is-open' : ''}`} style={{ width: 240, flexShrink: 0, background: 'var(--lms-sidebar)', padding: '18px 14px', display: 'flex', flexDirection: 'column', top: 0, height: '100vh', overflowY: 'auto' }}>
        {/* Top logo → brand homepage (leaves the LMS), not the LMS home. */}
        <div style={{ display: 'block', color: '#fff', padding: '4px 6px 22px' }}>
          <Brand size="md" linkToHome />
        </div>

        <div style={sectionLbl}>{t('Menu')}</div>
        <nav style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <NavItem to="/" end icon={HomeIcon} label={t('Home')} />
          <NavItem to="/courses" icon={BookOpen} label={t('Courses')} />
          <NavItem to="/my/courses" icon={GraduationCap} label={t('My courses')} />
          <NavItem to="/my/messages" icon={MessageCircle} label={t('Messages')} badge={msgUnread} />
          {isLoggedIn() && <NavItem to="/my/mentor" icon={CalendarClock} label={t('Mentor calendar')} />}
        </nav>

        <div style={{ ...sectionLbl, paddingTop: 22 }}>{t('Account')}</div>
        <nav style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <NavItem to="/my/wishlist" icon={Heart} label={t('Wishlist')} />
          <NavItem to="/my/purchases" icon={Receipt} label={t('Purchases')} />
          <NavItem to="/my/points" icon={Trophy} label={t('Points')} />
        </nav>

        {/* Phone: the topbar has no room for these — they live in the drawer. */}
        <div className="lms-only-phone" style={{ flexDirection: 'column', gap: 3, paddingTop: 18 }}>
          {staff && (
            <Link to="/admin/courses" style={{ display: 'flex', alignItems: 'center', padding: '11px', borderRadius: 6, fontSize: 13.5, fontWeight: 600, color: 'var(--lms-accent)', textDecoration: 'none' }}>
              {t('Back-office')}
            </Link>
          )}
          {isLoggedIn() && (
            <button type="button" onClick={signOut} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px', borderRadius: 6, fontSize: 13.5, fontWeight: 500, color: '#abb9e8', background: 'transparent', border: 0, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
              <LogOut size={17} /> {t('Sign out')}
            </button>
          )}
        </div>

        <div style={{ marginTop: 'auto', paddingTop: 14 }}>
          <Link to="/my/courses" style={{ display: 'block', textDecoration: 'none', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 8, padding: 13 }}>
            <div style={{ color: '#fff', fontSize: 12.5, fontWeight: 600 }}>{t('Continue learning')}</div>
            <div style={{ color: '#abb9e8', fontSize: 11, marginTop: 3, display: 'flex', alignItems: 'center', gap: 5 }}>
              {t('Jump back into your courses')} <ArrowRight size={13} />
            </div>
          </Link>
        </div>
      </aside>

      {navOpen && <div className="lms-side-scrim" onClick={() => setNavOpen(false)} aria-hidden="true" />}

      {/* ============ MAIN ============ */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Topbar */}
        <header className="lms-topbar" style={{ height: 60, flexShrink: 0, background: 'var(--lms-topbar)', borderBottom: '1px solid var(--lms-border)', display: 'flex', alignItems: 'center', gap: 18, position: 'sticky', top: 0, zIndex: 30 }}>
          <button type="button" className="lms-burger text-lms-muted hover:text-lms-text" onClick={() => setNavOpen(o => !o)} aria-label={t('Menu')} aria-expanded={navOpen}>
            {navOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <div className="lms-topbar-title" style={{ fontSize: 15, fontWeight: 600, color: 'var(--lms-text)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pageTitle}</div>
          <div className="lms-topbar-actions" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 }}>
            <ModeToggle />
            <button
              onClick={() => setLang(getLang() === 'es' ? 'en' : 'es')}
              className="lms-hit text-lms-muted hover:text-lms-text"
              style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: 0, cursor: 'pointer', fontSize: 12.5, fontWeight: 500, fontFamily: 'inherit' }}
            >
              <Languages size={15} />
              {getLang().toUpperCase()}
            </button>
            <NotificationsBell />
            <Link to="/cart" className="lms-hit text-lms-muted hover:text-lms-text" style={{ position: 'relative', display: 'flex' }} title={t('Cart')}>
              <ShoppingCart size={18} />
              {cartCount > 0 && (
                <span className="lms-num" style={{ position: 'absolute', top: -7, right: -8, minWidth: 15, height: 15, padding: '0 3px', borderRadius: 20, background: 'var(--lms-accent)', color: '#fff', fontSize: 9.5, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {cartCount}
                </span>
              )}
            </Link>
            {staff && (
              <Link to="/admin/courses" className="lms-hide-phone text-lms-accent hover:brightness-110" style={{ fontSize: 12.5, fontWeight: 600 }}>
                {t('Back-office')}
              </Link>
            )}
            <div className="lms-hide-phone" style={{ width: 1, height: 24, background: 'var(--lms-border)' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(10,179,156,0.12)', border: '1px solid rgba(10,179,156,0.35)', color: '#0a8f7e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: 13 }}>
                {(user?.name || '?').trim().slice(0, 1).toUpperCase()}
              </div>
              <div style={{ lineHeight: 1.15 }} className="hidden sm:block">
                <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--lms-text)' }}>{user?.name || ''}</div>
                <button onClick={signOut} className="text-lms-muted hover:text-lms-text" style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'transparent', border: 0, padding: 0, cursor: 'pointer', fontSize: 10.5, fontFamily: 'inherit' }}>
                  <LogOut size={11} /> {t('Sign out')}
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Pages with a full-width dark header band (course detail, My learning)
            lay out their own inner width; everything else gets the padded column. */}
        {fullBleed ? (
          <main style={{ flex: 1, minWidth: 0, background: 'var(--lms-surface)' }}>
            <Outlet />
          </main>
        ) : (
          <main className="lms-main" style={{ flex: 1, minWidth: 0 }}>
            <div style={{ maxWidth: 1200, margin: '0 auto' }}>
              <Outlet />
            </div>
          </main>
        )}

        <footer style={{ borderTop: '1px solid var(--lms-border)', background: 'var(--lms-surface)' }}>
          <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 24px', padding: '18px 24px' }}>
            <span style={{ fontSize: 12, color: 'var(--lms-faint)' }}>© {new Date().getFullYear()} Carlos Artiles</span>
            <div style={{ marginLeft: 'auto', display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
              <Link to="/instructor/apply" className="text-lms-muted hover:text-lms-text" style={{ fontSize: 12, padding: '10px 0' }}>{t('Become an instructor')}</Link>
              {pages.map((p) => (
                <Link key={p.key} to={`/p/${p.key}`} className="text-lms-muted hover:text-lms-text" style={{ fontSize: 12, padding: '10px 0' }}>{p.title}</Link>
              ))}
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
